import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import DisclaimerCard from './DisclaimerCard';
import TradingViewWidget from './TradingViewWidget';
import BullCallSpread from './BullCallSpread';
import BearPutSpread from './BearPutSpread';
import IronCondor from './IronCondor';

// Import timeframe constants
const TIMEFRAMES = {
    HOURLY: '1H',
    DAILY: '1D',
    WEEKLY: '1W'
};

interface StrategySuggestion {
    name: string;
    reasoning: string;
}

interface OptionStrategyFinderProps {
    initialTicker?: string;
    onTickerChange?: (ticker: string) => void;
}

const OptionStrategyFinder: React.FC<OptionStrategyFinderProps> = ({ initialTicker = '', onTickerChange }) => {
    const [ticker, setTicker] = useState(initialTicker);
    const [inputTicker, setInputTicker] = useState('');
    const [showTrading, setShowTrading] = useState(!!initialTicker);
    const [sentiment, setSentiment] = useState<'bullish' | 'bearish' | 'neutral' | null>(null);
    const [suggestion, setSuggestion] = useState<StrategySuggestion | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState<{[key: string]: any[]}>({});
    
    // Add function to fetch chart data when ticker changes
    const fetchChartData = async (symbol: string) => {
        if (!symbol) return;
        
        console.log(`Fetching chart data for ${symbol}`);
        
        try {
            // For TradingView widget, we don't need to fetch data from Alpaca anymore
            // But we still need to fetch current price for the Bull Call Spread component
            const response = await fetch(`/api/alpaca/quotes?symbol=${symbol}`);
            
            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data && data.quote && data.quote.ap) {
                setChartData({
                    [TIMEFRAMES.DAILY]: [{
                        close: data.quote.ap || 100 // Use ask price as current price or fallback to 100
                    }]
                });
            }
        } catch (err: any) {
            console.error('Error fetching quote data:', err);
            // If we can't get current price, set a default value
            setChartData({
                [TIMEFRAMES.DAILY]: [{
                    close: 100
                }]
            });
        }
    };

    // Add effect to fetch chart data when ticker changes
    useEffect(() => {
        if (ticker) {
            fetchChartData(ticker);
            // Reset sentiment when ticker changes to ensure strategy updates
            if (sentiment) {
                // Keep the current sentiment but trigger a refresh of the strategy
                const currentSentiment = sentiment;
                setSentiment(null);
                // Small delay to ensure the state updates before setting it again
                setTimeout(() => setSentiment(currentSentiment), 100);
            }
        }
    }, [ticker]);
    
    // Track TradingViewWidget symbol changes
    const handleSymbolChange = (newSymbol: string) => {
        if (newSymbol && newSymbol !== ticker) {
            // Extract just the symbol part if it includes exchange prefix (e.g. "NASDAQ:AAPL" -> "AAPL")
            const cleanSymbol = newSymbol.includes(':') ? newSymbol.split(':')[1] : newSymbol;
            setTicker(cleanSymbol);
            
            // Update UI state to show we have a valid ticker
            setShowTrading(true);
            
            // Notify parent component if callback exists
            if (onTickerChange) {
                onTickerChange(cleanSymbol);
            }
            
            console.log(`Symbol changed to ${cleanSymbol} via TradingView widget`);
        }
    };

    // Update ticker when initialTicker changes
    useEffect(() => {
        if (initialTicker && initialTicker !== ticker) {
            setTicker(initialTicker);
            setShowTrading(!!initialTicker);
        }
    }, [initialTicker]);

    useEffect(() => {
        if (ticker && sentiment) {
            setError(null);
            setSuggestion(null);
            setLoading(true);
            setTimeout(() => {
                if (sentiment === 'bullish') {
                    setSuggestion({
                        name: 'Bullish Strategy Suggestion',
                        reasoning: `Based on a bullish outlook for ${ticker.toUpperCase()}, consider strategies like selling cash-secured puts or implementing bull call spreads. These aim to capitalize on potential upward price movement while managing risk.`,
                    });
                } else if (sentiment === 'bearish') {
                    setSuggestion({
                        name: 'Bearish Strategy Suggestion',
                        reasoning: `Given a bearish sentiment for ${ticker.toUpperCase()}, strategies such as selling covered calls (if holding the underlying) or establishing bear put spreads could be suitable. These approaches seek to generate income or profit from potential downward price movement.`,
                    });
                } else if (sentiment === 'neutral') {
                    setSuggestion({
                        name: 'Neutral Strategy Suggestion',
                        reasoning: `With a neutral outlook for ${ticker.toUpperCase()}, consider strategies like iron condors or calendar spreads. These aim to profit from low volatility or time decay.`,
                    });
                }
                setLoading(false);
            }, 600);
        } else {
            setSuggestion(null);
            setError(null);
            setLoading(false);
        }
    }, [ticker, sentiment]);

    const handleSymbolSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (inputTicker.trim()) {
            setTicker(inputTicker.toUpperCase());
            setShowTrading(true);
            
            if (onTickerChange) {
                onTickerChange(inputTicker.toUpperCase());
            }
        }
    };

    return (
        <Card className="shadow-md">
            <CardHeader>
                <CardDescription>Enter a ticker symbol and select your market outlook to explore potential option strategies.</CardDescription>
            </CardHeader>

            {/* Moved this section here - to be between CardHeader and CardContent */}
            {showTrading && (
                <div className="px-6 pt-0 pb-6 space-y-6"> {/* Adjust padding as needed */}
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium">Select Your Market Outlook</h3>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                                setShowTrading(false);
                                setInputTicker('');
                                setSentiment(null);
                                setSuggestion(null);
                            }}
                        >
                            Change Symbol
                        </Button>
                    </div>
                    
                    <RadioGroup
                        value={sentiment ?? undefined}
                        onValueChange={(value: 'bullish' | 'bearish' | 'neutral') => setSentiment(value)}
                        className="flex flex-row space-x-6 justify-center pt-2 items-center"
                    >
                        <div className="flex items-center justify-center">
                            <RadioGroupItem value="bullish" id="bullish" className="mr-2" />
                            <Label htmlFor="bullish" className="font-normal">Bullish</Label>
                        </div>
                        <div className="flex items-center justify-center">
                            <RadioGroupItem value="bearish" id="bearish" className="mr-2" />
                            <Label htmlFor="bearish" className="font-normal">Bearish</Label>
                        </div>
                        <div className="flex items-center justify-center">
                            <RadioGroupItem value="neutral" id="neutral" className="mr-2" />
                            <Label htmlFor="neutral" className="font-normal">Neutral</Label>
                        </div>
                    </RadioGroup>
                </div>
            )}

            <CardContent className="space-y-6">
                {!showTrading ? (
                    <form onSubmit={handleSymbolSubmit} className="space-y-6">
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="w-full max-w-md space-y-4">
                                <h3 className="text-lg font-medium text-center">Enter a Ticker Symbol</h3>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Enter ticker symbol (e.g., AAPL)"
                                        value={inputTicker}
                                        onChange={(e) => setInputTicker(e.target.value.toUpperCase())}
                                        className="flex-1"
                                        autoFocus
                                    />
                                    <Button type="submit">
                                        <Search className="h-4 w-4 mr-2" />
                                        Show Chart
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </form>
                ) : (
                    <>
                        {/* Trading View Widget */}
                        <div className="mb-6" style={{ height: "450px" }}>
                            <TradingViewWidget 
                                symbol={ticker} 
                                range="1M"
                                // onSymbolChange={handleSymbolChange} // Ensure this is commented out or removed if TradingViewWidget doesn't accept it
                            />
                        </div>

                        {/* The section that was here for "Select Your Market Outlook" when showTrading was true has been moved above CardContent */}

                        {loading && (
                            <div className="flex justify-center items-center pt-4">
                                <div className="inline-block animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full"></div>
                                <span className="ml-2 text-sm text-gray-600">Analyzing...</span>
                            </div>
                        )}

                        {error && <p className="text-red-600 text-sm">{error}</p>}

                        {suggestion && (
                            <Card className="mt-6 bg-gray-50 border border-gray-200">
                                <CardHeader>
                                    <CardTitle className="text-lg">{suggestion.name}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-gray-700"><strong>Rationale:</strong> {suggestion.reasoning}</p>
                                </CardContent>
                            </Card>
                        )}

                        <DisclaimerCard />
                        
                        {/* Add Bull Call Spread visualization when ticker and bullish sentiment */}
                        {ticker && sentiment === 'bullish' && (
                            <div className="mt-6">
                                <BullCallSpread 
                                    symbol={ticker} 
                                    currentPrice={chartData?.[TIMEFRAMES.DAILY]?.length > 0 ? 
                                        chartData[TIMEFRAMES.DAILY][chartData[TIMEFRAMES.DAILY].length-1]?.close : 
                                        undefined} 
                                />
                            </div>
                        )}

                        {/* Add Bear Put Spread visualization when ticker and bearish sentiment */}
                        {ticker && sentiment === 'bearish' && (
                            <div className="mt-6">
                                <BearPutSpread 
                                    symbol={ticker} 
                                    currentPrice={chartData?.[TIMEFRAMES.DAILY]?.length > 0 ? 
                                        chartData[TIMEFRAMES.DAILY][chartData[TIMEFRAMES.DAILY].length-1]?.close : 
                                        undefined} 
                                />
                            </div>
                        )}

                        {/* Add Iron Condor visualization when ticker and neutral sentiment */}
                        {ticker && sentiment === 'neutral' && (
                            <div className="mt-6">
                                <IronCondor 
                                    symbol={ticker} 
                                    currentPrice={chartData?.[TIMEFRAMES.DAILY]?.length > 0 ? 
                                        chartData[TIMEFRAMES.DAILY][chartData[TIMEFRAMES.DAILY].length-1]?.close : 
                                        undefined} 
                                />
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default OptionStrategyFinder;
